// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Indicator } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { Patient } from '@medplum/fhirtypes';
import { ErrorBoundary, Loading, useMedplum, useMedplumProfile } from '@medplum/react';
import { NotificationBell } from '@polarissleep/messaging';
import { isPendingIntakePatient, NotificationAudience } from '@polarissleep/shared';
import { IconCalendar, IconClipboardData, IconMessage, IconSettings } from '@tabler/icons-react';
import type { JSX, ReactNode } from 'react';
import { Suspense, useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router';
import { FunnelAnalytics } from './analytics/FunnelAnalytics';
import { captureRegisterAttribution } from './analytics/funnel';
import { AppShell } from './components/layout/AppShell';
import { DeferredIntakeQuestionnaireModal } from './components/onboarding/DeferredIntakeQuestionnaireModal';
import { getConfig } from './config';
import { CONSENT_PENDING_TAG } from './lib/constants';
import { useNotifications } from './notifications/useNotifications';
import { AppointmentsPage } from './pages/AppointmentsPage';
import { ConsentPage } from './pages/ConsentPage';
import { DocumentPage } from './pages/DocumentPage';
import { EligibilityPage } from './pages/EligibilityPage';
import { ExistingPatientIntakePage } from './pages/ExistingPatientIntakePage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { HsatResultsPage } from './pages/HsatResultsPage';
import { IntakeV3AnswersProvider } from './pages/intake-v3/answers';
import { IntakeV3Page } from './pages/intake-v3/IntakeV3Page';
import { INTAKE_V3_BASE_PATH, INTAKE_V3_ENTRY_PATH, isIntakeV3Path } from './pages/intake-v3/steps';
import { MessagesPage } from './pages/messages/MessagesPage';
import { DiagnosticReportsPage } from './pages/my-chart/DiagnosticReportsPage';
import { IntakeScreenerPage } from './pages/my-chart/IntakeScreenerPage';
import { MyChartPage } from './pages/my-chart/MyChartPage';
import { PrescriptionDocumentPage } from './pages/my-chart/PrescriptionDocumentPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { OrderHSATPage } from './pages/OrderHSATPage';
import { PatientDashboardPage } from './pages/PatientDashboardPage';
import { PayAppointmentPage } from './pages/PayAppointmentPage';
import { PayChargePage } from './pages/PayChargePage';
import { ReschedulePage } from './pages/ReschedulePage';
import { SchedulingPage } from './pages/SchedulingPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { SignInPage } from './pages/SignInPage';
import { AccountSettingsPage } from './pages/settings/AccountSettingsPage';
import { SettingsTaskProvider } from './pages/settings/SettingsTaskContext';
import { useSettingsTaskCount, useSettingsTaskLoaded } from './pages/settings/useSettingsTask';

function hasConsentPendingTag(profile: Patient): boolean {
  return (
    profile.meta?.tag?.some(
      (tag) => tag.system === CONSENT_PENDING_TAG.system && tag.code === CONSENT_PENDING_TAG.code
    ) ?? false
  );
}

interface RequireConsentProps {
  children: ReactNode;
}

function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const profile = useMedplumProfile();

  if (!profile) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
}

function RequireConsent({ children }: RequireConsentProps): JSX.Element {
  const profile = useMedplumProfile() as Patient | undefined;
  const location = useLocation();

  // If the profile is a Patient with consent-pending tag, redirect to consent page
  if (profile?.resourceType === 'Patient' && hasConsentPendingTag(profile) && location.pathname !== '/consent') {
    return <Navigate to="/consent" replace />;
  }

  return <>{children}</>;
}

function RequireIntake({ children }: RequireConsentProps): JSX.Element {
  const profile = useMedplumProfile() as Patient | undefined;
  const location = useLocation();
  const onboardingId = profile?.identifier?.find(
    (identifier) => identifier.system === 'http://polarissleep.com/onboarding-id'
  )?.value;
  const eligibilityPath = onboardingId ? `/onboarding/${onboardingId}` : '/onboarding';

  if (
    isPendingIntakePatient(profile) &&
    location.pathname !== '/onboarding' &&
    location.pathname !== '/consent' &&
    location.pathname !== eligibilityPath
  ) {
    return <Navigate to={eligibilityPath} replace />;
  }

  return <>{children}</>;
}

export function IntakeV3RegisterRedirect(): JSX.Element {
  const location = useLocation();

  // FunnelAnalytics never mounts on this path, so capture UTM attribution before the query string leaves /register.
  useEffect(() => {
    if (getConfig().patientAppGa4MeasurementId) {
      captureRegisterAttribution(new URL(`${location.pathname}${location.search}`, window.location.origin));
    }
  }, [location.pathname, location.search]);

  return <Navigate to={`${INTAKE_V3_ENTRY_PATH}${location.search}`} replace />;
}

export function App(): JSX.Element | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const location = useLocation();
  const wrongProfileType = !!profile && profile.resourceType !== 'Patient';

  useEffect(() => {
    if (!wrongProfileType) {
      return;
    }
    showNotification({
      color: 'red',
      title: 'Access denied',
      message: 'This account is not a patient account. Please use the provider app.',
    });
    medplum
      .signOut()
      .catch(console.error)
      .finally(() => {
        window.location.href = '/';
      });
  }, [wrongProfileType, medplum]);

  if (medplum.isLoading()) {
    return null;
  }

  if (wrongProfileType && location.pathname !== '/signin') {
    return <Navigate to="/signin" replace />;
  }

  // Render public pages outside of AppShell (no authentication required)
  if (location.pathname === '/register') {
    if (getConfig().intakeV3Enabled) {
      return <IntakeV3RegisterRedirect />;
    }
    return (
      <ErrorBoundary>
        <FunnelAnalytics />
        <Suspense fallback={<Loading />}>
          <OnboardingPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (isIntakeV3Path(location.pathname)) {
    if (!getConfig().intakeV3Enabled) {
      return <Navigate to="/register" replace />;
    }
    return (
      <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <IntakeV3AnswersProvider>
            <Routes>
              <Route path={`${INTAKE_V3_BASE_PATH}/:step?`} element={<IntakeV3Page />} />
            </Routes>
          </IntakeV3AnswersProvider>
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (location.pathname === '/set-password') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <SetPasswordPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (location.pathname === '/forgot-password') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <ForgotPasswordPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (location.pathname.startsWith('/onboarding/')) {
    return (
      <ErrorBoundary>
        <FunnelAnalytics />
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/onboarding/:uniqueId" element={<EligibilityPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <SettingsTaskProvider>
      <AppContent />
    </SettingsTaskProvider>
  );
}

function AppContent(): JSX.Element {
  const profile = useMedplumProfile() as Patient | undefined;
  const location = useLocation();
  const navigate = useNavigate();
  const [intakeModalOpen, setIntakeModalOpen] = useState(
    () => new URLSearchParams(location.search).get('openIntakeQuestionnaire') === 'true'
  );
  useEffect(() => {
    setIntakeModalOpen(new URLSearchParams(location.search).get('openIntakeQuestionnaire') === 'true');
  }, [location.search]);
  const settingsTaskCount = useSettingsTaskCount();
  const settingsTaskLoaded = useSettingsTaskLoaded();
  const notificationsResult = useNotifications();

  const settingsIcon =
    settingsTaskLoaded && settingsTaskCount > 0 ? (
      <Indicator label={settingsTaskCount} size={16} color="red">
        <IconSettings />
      </Indicator>
    ) : (
      <IconSettings />
    );

  return (
    <>
      <AppShell
        notifications={
          profile ? (
            <NotificationBell
              notificationsResult={notificationsResult}
              audience={NotificationAudience.PATIENT}
              viewAllHref="/notifications"
              navigate={navigate}
            />
          ) : undefined
        }
        menus={
          profile
            ? [
                {
                  links: [
                    { icon: <IconMessage />, label: 'Messages', href: '/messages' },
                    { icon: <IconCalendar />, label: 'Appointments', href: '/appointments' },
                    { icon: <IconClipboardData />, label: 'My Chart', href: '/my-chart' },
                    { icon: settingsIcon, label: 'Settings', href: '/settings' },
                  ],
                },
              ]
            : undefined
        }
      >
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>
            <RequireConsent>
              <RequireIntake>
                <Routes>
                  <Route path="/" element={profile ? <PatientDashboardPage /> : <Navigate to="/signin" replace />} />
                  <Route path="/home" element={<Navigate to="/" replace />} />
                  <Route path="/signin" element={<SignInPage />} />
                  <Route path="/consent" element={<ConsentPage />} />
                  <Route path="/appointments" element={<AppointmentsPage />} />
                  <Route path="/appointments/schedule" element={<SchedulingPage />} />
                  <Route path="/my-chart" element={<MyChartPage />} />
                  <Route path="/my-chart/prescriptions/:prescriptionId" element={<PrescriptionDocumentPage />} />
                  <Route path="/my-chart/intake-screener" element={<IntakeScreenerPage />} />
                  <Route path="/my-chart/results" element={<DiagnosticReportsPage />} />
                  <Route path="/my-chart/results/:serviceRequestId" element={<DiagnosticReportsPage />} />
                  <Route path="/appointments/reschedule/:appointmentId" element={<ReschedulePage />} />
                  <Route path="/onboarding" element={<ExistingPatientIntakePage />} />
                  <Route
                    element={
                      <RequireAuth>
                        <Outlet />
                      </RequireAuth>
                    }
                  >
                    <Route path="/order-hsat/:serviceRequestId" element={<OrderHSATPage />} />
                    <Route path="/pay/:appointmentId" element={<PayAppointmentPage />} />
                    <Route path="/pay-charge/:chargeItemId" element={<PayChargePage />} />
                    <Route path="/documents/:documentReferenceId" element={<DocumentPage />} />
                    <Route path="/messages/:threadId?" element={<MessagesPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/settings" element={<AccountSettingsPage />} />
                  </Route>
                </Routes>
              </RequireIntake>
            </RequireConsent>
          </Suspense>
        </ErrorBoundary>
      </AppShell>
      <DeferredIntakeQuestionnaireModal
        opened={intakeModalOpen}
        uniqueId={
          profile?.identifier?.find((identifier) => identifier.system === 'http://polarissleep.com/onboarding-id')
            ?.value
        }
        onClose={() => {
          setIntakeModalOpen(false);
          navigate('/', { replace: true });
        }}
      />
    </>
  );
}
